import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import { db } from '../db/indexedDb';
import { buildArtworkDescriptor, clampPosition, createObjectUrl, revokeObjectUrl } from '../lib/audio';
import type { RepeatMode, Track } from '../types';

interface PlayerContextValue {
  queue: string[];
  currentIndex: number;
  currentTrack?: Track;
  isPlaying: boolean;
  duration: number;
  currentTime: number;
  repeatMode: RepeatMode;
  shuffleEnabled: boolean;
  isQueueOpen: boolean;
  enqueue: (trackIds: string[]) => void;
  playTrack: (trackId: string, nextQueue?: string[]) => Promise<void>;
  playPause: () => void;
  seek: (time: number) => void;
  next: () => Promise<void>;
  previous: () => Promise<void>;
  setQueueOpen: (open: boolean) => void;
  reorderQueue: (from: number, to: number) => void;
  removeFromQueue: (trackId: string) => void;
  clearQueue: () => void;
  playNext: (trackId: string) => void;
  setRepeatMode: (mode: RepeatMode) => Promise<void>;
  toggleShuffle: () => Promise<void>;
}

const PlayerContext = createContext<PlayerContextValue | null>(null);

export function PlayerProvider({ children }: { children: React.ReactNode }) {
  const audioRef = useRef<HTMLAudioElement>(new Audio());
  const currentUrlRef = useRef<string>();
  const frameRef = useRef<number>();
  const [queue, setQueue] = useState<string[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [currentTrack, setCurrentTrack] = useState<Track>();
  const [isPlaying, setIsPlaying] = useState(false);
  const [duration, setDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [repeatMode, setRepeatModeState] = useState<RepeatMode>('off');
  const [shuffleEnabled, setShuffleEnabled] = useState(false);
  const [isQueueOpen, setQueueOpen] = useState(false);

  const persistSession = useCallback(
    async (overrides?: Partial<{ queue: string[]; currentIndex: number; lastTrackId?: string; lastPosition: number }>) => {
      const activeTrackId = overrides?.lastTrackId ?? queue[currentIndex];
      await db.updateSettings({
        queue: {
          trackIds: overrides?.queue ?? queue,
          currentIndex: overrides?.currentIndex ?? currentIndex
        },
        lastTrackId: activeTrackId,
        lastPosition: overrides?.lastPosition ?? audioRef.current.currentTime,
        repeatMode,
        shuffleEnabled
      });
    },
    [currentIndex, queue, repeatMode, shuffleEnabled]
  );

  const syncMediaSession = useCallback(
    async (track?: Track) => {
      if (!('mediaSession' in navigator) || !track) return;

      const artBlob = track.artworkBlobId ? await db.getBlob(track.artworkBlobId) : undefined;
      const artworkUrl = createObjectUrl(artBlob);
      const placeholder = buildArtworkDescriptor(track);
      navigator.mediaSession.metadata = new MediaMetadata({
        title: track.title,
        artist: track.artist,
        album: track.album,
        artwork: [
          {
            src:
              artworkUrl ||
              `data:image/svg+xml,${encodeURIComponent(
                `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 320 320"><rect width="320" height="320" fill="${placeholder.gradient.includes('hsl') ? '#1db954' : '#0f172a'}"/><text x="50%" y="52%" dominant-baseline="middle" text-anchor="middle" fill="white" font-size="96" font-family="sans-serif">${placeholder.label}</text></svg>`
              )}`,
            sizes: '512x512',
            type: artworkUrl ? artBlob?.blob.type || 'image/jpeg' : 'image/svg+xml'
          }
        ]
      });
    },
    []
  );

  const stepFrame = useCallback(() => {
    setCurrentTime(audioRef.current.currentTime || 0);
    frameRef.current = window.requestAnimationFrame(stepFrame);
  }, []);

  const loadAndPlay = useCallback(
    async (trackId: string, nextQueue?: string[]) => {
      const track = await db.tracks.get(trackId);
      if (!track) return;

      const audioBlob = await db.getBlob(track.audioBlobId);
      if (!audioBlob) return;

      revokeObjectUrl(currentUrlRef.current);
      currentUrlRef.current = createObjectUrl(audioBlob);
      audioRef.current.src = currentUrlRef.current ?? '';
      audioRef.current.load();

      if (nextQueue) {
        const index = nextQueue.findIndex((id) => id === trackId);
        setQueue(nextQueue);
        setCurrentIndex(index >= 0 ? index : 0);
      } else {
        setCurrentIndex((index) => {
          const existing = queue.findIndex((id) => id === trackId);
          return existing >= 0 ? existing : index;
        });
      }

      setCurrentTrack(track);
      setDuration(track.duration);
      setCurrentTime(0);
      await syncMediaSession(track);
      await audioRef.current.play();
      setIsPlaying(true);
      await persistSession({
        queue: nextQueue ?? queue,
        currentIndex: nextQueue ? Math.max(nextQueue.indexOf(trackId), 0) : currentIndex,
        lastTrackId: trackId,
        lastPosition: 0
      });
    },
    [currentIndex, persistSession, queue, syncMediaSession]
  );

  useEffect(() => {
    void db.ensureDefaults();

    void (async () => {
      const settings = await db.getAppSettings();
      setQueue(settings.queue.trackIds);
      setCurrentIndex(settings.queue.currentIndex);
      setRepeatModeState(settings.repeatMode);
      setShuffleEnabled(settings.shuffleEnabled);

      if (settings.lastTrackId) {
        const track = await db.tracks.get(settings.lastTrackId);
        if (track) {
          setCurrentTrack(track);
          setDuration(track.duration);
          await syncMediaSession(track);
        }
      }
    })();
  }, [syncMediaSession]);

  const handleAdvance = useCallback(
    async (direction: 1 | -1) => {
      if (!queue.length) {
        audioRef.current.pause();
        return;
      }

      const nextIndexBase =
        shuffleEnabled && queue.length > 1
          ? Math.floor(Math.random() * queue.length)
          : currentIndex + direction;

      if (nextIndexBase < 0) {
        if (repeatMode === 'all') {
          await loadAndPlay(queue.at(-1)!, queue);
        }
        return;
      }

      if (nextIndexBase >= queue.length) {
        if (repeatMode === 'all') {
          await loadAndPlay(queue[0], queue);
        } else {
          audioRef.current.pause();
          setIsPlaying(false);
        }
        return;
      }

      await loadAndPlay(queue[nextIndexBase], queue);
    },
    [currentIndex, loadAndPlay, queue, repeatMode, shuffleEnabled]
  );

  useEffect(() => {
    const audio = audioRef.current;

    const onLoadedMetadata = () => setDuration(audio.duration || currentTrack?.duration || 0);
    const onPlay = () => {
      setIsPlaying(true);
      frameRef.current = window.requestAnimationFrame(stepFrame);
    };
    const onPause = () => {
      setIsPlaying(false);
      if (frameRef.current) cancelAnimationFrame(frameRef.current);
      void persistSession();
    };
    const onEnded = () => {
      if (repeatMode === 'one' && currentTrack) {
        audio.currentTime = 0;
        void audio.play();
        return;
      }
      void handleAdvance(1);
    };

    audio.addEventListener('loadedmetadata', onLoadedMetadata);
    audio.addEventListener('play', onPlay);
    audio.addEventListener('pause', onPause);
    audio.addEventListener('ended', onEnded);

    return () => {
      audio.removeEventListener('loadedmetadata', onLoadedMetadata);
      audio.removeEventListener('play', onPlay);
      audio.removeEventListener('pause', onPause);
      audio.removeEventListener('ended', onEnded);
      if (frameRef.current) cancelAnimationFrame(frameRef.current);
    };
  }, [currentTrack, handleAdvance, persistSession, repeatMode, stepFrame]);

  useEffect(() => {
    return () => {
      revokeObjectUrl(currentUrlRef.current);
    };
  }, []);

  useEffect(() => {
    if (!('mediaSession' in navigator)) return;
    navigator.mediaSession.setActionHandler('play', () => void audioRef.current.play());
    navigator.mediaSession.setActionHandler('pause', () => audioRef.current.pause());
    navigator.mediaSession.setActionHandler('previoustrack', () => void handleAdvance(-1));
    navigator.mediaSession.setActionHandler('nexttrack', () => void handleAdvance(1));
    navigator.mediaSession.setActionHandler('seekto', (details) => {
      if (typeof details.seekTime === 'number') {
        audioRef.current.currentTime = clampPosition(details.seekTime, duration);
        setCurrentTime(audioRef.current.currentTime);
      }
    });
  }, [duration, handleAdvance]);

  const playPause = useCallback(() => {
    if (!audioRef.current.src && queue.length > 0) {
      void loadAndPlay(queue[currentIndex] ?? queue[0], queue);
      return;
    }
    if (audioRef.current.paused) {
      void audioRef.current.play();
    } else {
      audioRef.current.pause();
    }
  }, [currentIndex, loadAndPlay, queue]);

  const enqueue = useCallback(
    (trackIds: string[]) => {
      if (!trackIds.length) return;
      setQueue((current) => {
        const next = [...current, ...trackIds];
        void persistSession({ queue: next });
        return next;
      });
    },
    [persistSession]
  );

  const playTrack = useCallback(
    async (trackId: string, nextQueue?: string[]) => {
      const queueToUse = nextQueue ?? (queue.includes(trackId) ? queue : [trackId, ...queue]);
      await loadAndPlay(trackId, queueToUse);
    },
    [loadAndPlay, queue]
  );

  const seek = useCallback((time: number) => {
    audioRef.current.currentTime = clampPosition(time, duration);
    setCurrentTime(audioRef.current.currentTime);
  }, [duration]);

  const reorderQueue = useCallback(
    (from: number, to: number) => {
      setQueue((current) => {
        const next = [...current];
        const [item] = next.splice(from, 1);
        next.splice(to, 0, item);
        const activeTrackId = current[currentIndex];
        const nextCurrentIndex = next.findIndex((id) => id === activeTrackId);
        setCurrentIndex(nextCurrentIndex >= 0 ? nextCurrentIndex : 0);
        void persistSession({ queue: next, currentIndex: nextCurrentIndex >= 0 ? nextCurrentIndex : 0 });
        return next;
      });
    },
    [currentIndex, persistSession]
  );

  const removeFromQueue = useCallback(
    (trackId: string) => {
      setQueue((current) => {
        const next = current.filter((id) => id !== trackId);
        const nextCurrentIndex = Math.min(currentIndex, Math.max(next.length - 1, 0));
        setCurrentIndex(nextCurrentIndex);
        void persistSession({ queue: next, currentIndex: nextCurrentIndex });
        return next;
      });
    },
    [currentIndex, persistSession]
  );

  const clearQueue = useCallback(() => {
    setQueue([]);
    setCurrentIndex(0);
    audioRef.current.pause();
    audioRef.current.src = '';
    setCurrentTrack(undefined);
    void persistSession({ queue: [], currentIndex: 0, lastTrackId: undefined, lastPosition: 0 });
  }, [persistSession]);

  const playNext = useCallback(
    (trackId: string) => {
      setQueue((current) => {
        const next = [...current];
        next.splice(currentIndex + 1, 0, trackId);
        void persistSession({ queue: next });
        return next;
      });
    },
    [currentIndex, persistSession]
  );

  const setRepeatMode = useCallback(async (mode: RepeatMode) => {
    setRepeatModeState(mode);
    await db.updateSettings({ repeatMode: mode });
  }, []);

  const toggleShuffle = useCallback(async () => {
    setShuffleEnabled((current) => {
      void db.updateSettings({ shuffleEnabled: !current });
      return !current;
    });
  }, []);

  const value = useMemo<PlayerContextValue>(
    () => ({
      queue,
      currentIndex,
      currentTrack,
      isPlaying,
      duration,
      currentTime,
      repeatMode,
      shuffleEnabled,
      isQueueOpen,
      enqueue,
      playTrack,
      playPause,
      seek,
      next: () => handleAdvance(1),
      previous: () => handleAdvance(-1),
      setQueueOpen,
      reorderQueue,
      removeFromQueue,
      clearQueue,
      playNext,
      setRepeatMode,
      toggleShuffle
    }),
    [
      clearQueue,
      currentIndex,
      currentTime,
      currentTrack,
      duration,
      enqueue,
      handleAdvance,
      isPlaying,
      isQueueOpen,
      playNext,
      playPause,
      playTrack,
      queue,
      removeFromQueue,
      repeatMode,
      reorderQueue,
      seek,
      setRepeatMode,
      shuffleEnabled,
      toggleShuffle
    ]
  );

  return <PlayerContext.Provider value={value}>{children}</PlayerContext.Provider>;
}

export function usePlayer() {
  const context = useContext(PlayerContext);
  if (!context) {
    throw new Error('usePlayer must be used within PlayerProvider');
  }
  return context;
}
